/**
 * Mail composer for 書類審査結果通知 — layout and behaviour copied from CreateInvitationStep3 (separate file, not imported).
 */
import React, { useState, useEffect } from 'react';
import './ResubmissionEmailComposer.css';
import backButtonIcon from '../../../../../assests/icons/back-button.png';
import editIcon from '../../../../../assests/icons/edit_icon.png';
import { getPlaceholderWarnings, replaceDeadlineLineInBody, type ResubmissionEmailMode } from './emailTemplates';

export interface ResubmissionEmailComposerProps {
  employeeName: string;
  employeeEmail: string;
  mode: ResubmissionEmailMode;
  initialSubject: string;
  initialBody: string;
  defaultSubject: string;
  defaultBody: string;
  /** yyyy-mm-dd when mode is rejected */
  initialDeadlineIso: string | null;
  onClose: () => void;
  onSend: (payload: { subject: string; body: string; deadline: string | null }) => Promise<void>;
  sending?: boolean;
}

const ResubmissionEmailComposer: React.FC<ResubmissionEmailComposerProps> = ({
  employeeName,
  employeeEmail,
  mode,
  initialSubject,
  initialBody,
  defaultSubject,
  defaultBody,
  initialDeadlineIso,
  onClose,
  onSend,
  sending = false,
}) => {
  const [deadline, setDeadline] = useState(
    () => initialDeadlineIso ?? new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10)
  );
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [subjectEditable, setSubjectEditable] = useState(false);
  const [bodyEditable, setBodyEditable] = useState(false);
  const [editChoiceTarget, setEditChoiceTarget] = useState<'subject' | 'body' | null>(null);
  const [editChoicePhase, setEditChoicePhase] = useState<'choice' | 'warning'>('choice');

  /** Keep「再提出期限」in メール本文 in sync with the date picker (rejected flow). */
  useEffect(() => {
    if (mode !== 'rejected') return;
    setBody((prev) => replaceDeadlineLineInBody(prev, deadline));
  }, [deadline, mode]);

  const handleDeadlineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setDeadline(next);
  };

  const handleEditSubjectClick = () => {
    if (subjectEditable) {
      setSubjectEditable(false);
      return;
    }
    setEditChoiceTarget('subject');
    setEditChoicePhase('choice');
  };

  const handleEditBodyClick = () => {
    if (bodyEditable) {
      setBodyEditable(false);
      return;
    }
    setEditChoiceTarget('body');
    setEditChoicePhase('choice');
  };

  const handleEditChoiceSetDefault = () => {
    if (editChoiceTarget === 'subject') {
      setSubject(defaultSubject);
    } else if (editChoiceTarget === 'body') {
      setBody(defaultBody);
      if (mode === 'rejected' && initialDeadlineIso) {
        setDeadline(initialDeadlineIso);
      }
    }
    setEditChoiceTarget(null);
  };

  const handleEditChoiceEditContent = () => {
    setEditChoicePhase('warning');
  };

  const handleEditChoiceStartEditing = () => {
    if (editChoiceTarget === 'subject') {
      setSubjectEditable(true);
    } else if (editChoiceTarget === 'body') {
      setBodyEditable(true);
    }
    setEditChoiceTarget(null);
  };

  const handleEditChoiceClose = () => {
    setEditChoiceTarget(null);
    setEditChoicePhase('choice');
  };

  const handleSend = async () => {
    const bodyToSend =
      mode === 'rejected' ? replaceDeadlineLineInBody(body, deadline) : body;
    await onSend({
      subject,
      body: bodyToSend,
      deadline: mode === 'rejected' ? deadline : null,
    });
  };

  const subjectWarning = getPlaceholderWarnings(mode, 'subject');
  const bodyWarning = getPlaceholderWarnings(mode, 'body');

  return (
    <div className="resubmission-email">
      <div className="resubmission-email__header">
        <button
          type="button"
          className="resubmission-email__back-btn"
          onClick={onClose}
          aria-label="閉じる"
        >
          <img src={backButtonIcon} alt="" className="resubmission-email__back-icon" aria-hidden />
        </button>
        <h1 id="resubmission-email-title" className="resubmission-email__title">
          書類審査結果のメール送信
        </h1>
      </div>

      <div className="resubmission-email__content">
        <div className="resubmission-email__top-row">
          <div className="resubmission-email__row">
            <label className="resubmission-email__label">宛先:</label>
            <div className="resubmission-email__recipient-wrap">
              <select
                className="resubmission-email__recipient-select"
                value={employeeEmail}
                disabled
                aria-label="宛先"
              >
                <option value={employeeEmail}>
                  {employeeName} : {employeeEmail}
                </option>
              </select>
            </div>
          </div>
          {mode === 'rejected' && (
            <div className="resubmission-email__row">
              <label className="resubmission-email__label">提出期限:</label>
              <div className="resubmission-email__date-wrap">
                <input
                  type="date"
                  className="resubmission-email__date-input"
                  value={deadline}
                  onChange={handleDeadlineChange}
                  aria-label="再提出期限"
                />
              </div>
            </div>
          )}
        </div>

        <div className="resubmission-email__subject-row">
          <label className="resubmission-email__label">件名:</label>
          <input
            type="text"
            className="resubmission-email__subject-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            readOnly={!subjectEditable}
            aria-label="件名"
          />
          <button
            type="button"
            className="resubmission-email__edit-btn"
            onClick={handleEditSubjectClick}
            aria-label={subjectEditable ? '編集を完了' : '件名を編集'}
          >
            {subjectEditable ? '完了' : '編集'}
          </button>
        </div>

        <div className="resubmission-email__field resubmission-email__field--body">
          <div className="resubmission-email__body-header">
            <label className="resubmission-email__label">メール本文:</label>
            <button
              type="button"
              className="resubmission-email__edit-btn resubmission-email__edit-btn--body"
              onClick={handleEditBodyClick}
              aria-label={bodyEditable ? '編集を完了' : '本文を編集'}
            >
              <img src={editIcon} alt="" className="resubmission-email__edit-icon" aria-hidden />
              {bodyEditable ? '完了' : '編集'}
            </button>
          </div>
          <textarea
            className="resubmission-email__body-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            readOnly={!bodyEditable}
            rows={14}
            aria-label="メール本文"
          />
        </div>
      </div>

      <div className="resubmission-email__footer">
        <button type="button" className="resubmission-email__back-footer-btn" onClick={onClose}>
          提出確認へ戻る
        </button>
        <button
          type="button"
          className="resubmission-email__send-btn"
          onClick={handleSend}
          disabled={sending}
        >
          {sending ? '送信中...' : '送信する'}
        </button>
      </div>

      {editChoiceTarget && (
        <div className="resubmission-email__modal-overlay" onClick={handleEditChoiceClose}>
          <div className="resubmission-email__modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="resubmission-email__modal-title">
              {editChoiceTarget === 'subject' ? '件名' : 'メール本文'}
            </h3>
            {editChoicePhase === 'choice' ? (
              <>
                <p className="resubmission-email__modal-text">
                  デフォルトに戻しますか、それとも内容を編集しますか？
                </p>
                <div className="resubmission-email__modal-actions">
                  <button
                    type="button"
                    className="resubmission-email__modal-btn resubmission-email__modal-btn--default"
                    onClick={handleEditChoiceSetDefault}
                  >
                    デフォルトに戻す
                  </button>
                  <button
                    type="button"
                    className="resubmission-email__modal-btn resubmission-email__modal-btn--edit"
                    onClick={handleEditChoiceEditContent}
                  >
                    内容を編集
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="resubmission-email__modal-text resubmission-email__modal-text--warning">
                  {editChoiceTarget === 'subject' ? subjectWarning : bodyWarning}
                </p>
                <div className="resubmission-email__modal-actions">
                  <button
                    type="button"
                    className="resubmission-email__modal-btn resubmission-email__modal-btn--edit"
                    onClick={handleEditChoiceStartEditing}
                  >
                    編集を開始
                  </button>
                </div>
              </>
            )}
            <button
              type="button"
              className="resubmission-email__modal-close"
              onClick={handleEditChoiceClose}
              aria-label="閉じる"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResubmissionEmailComposer;
